/**
 * Inventories Filters Component
 *
 * Filter controls for the inventories list.
 * @see US-12.6 - Page Liste
 */

import { Search } from 'lucide-react';
import { Input, Select, DatePicker } from '@/components/ui';
import type { DateRange } from '@/components/ui';
import {
  InventoryStatus,
  InventoryType,
  INVENTORY_STATUS_LABELS,
  INVENTORY_TYPE_LABELS,
} from '@bstock/shared';

interface InventoriesFiltersProps {
  status: string;
  type: string;
  searchQuery: string;
  dateRange: DateRange;
  onStatusChange: (status: string) => void;
  onTypeChange: (type: string) => void;
  onSearchChange: (query: string) => void;
  onDateRangeChange: (range: DateRange) => void;
}

const statusOptions = [
  { value: '', label: 'Tous les statuts' },
  ...Object.values(InventoryStatus).map((status) => ({
    value: status,
    label: INVENTORY_STATUS_LABELS[status],
  })),
];

const typeOptions = [
  { value: '', label: 'Tous les types' },
  ...Object.values(InventoryType).map((type) => ({
    value: type,
    label: INVENTORY_TYPE_LABELS[type],
  })),
];

export function InventoriesFilters({
  status,
  type,
  searchQuery,
  dateRange,
  onStatusChange,
  onTypeChange,
  onSearchChange,
  onDateRangeChange,
}: InventoriesFiltersProps) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Date range - en premier a droite */}
      <DatePicker
        value={dateRange}
        onChange={onDateRangeChange}
      />

      {/* Search */}
      <div className="relative flex-1 min-w-[200px] max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 z-10" />
        <Input
          type="text"
          placeholder="Rechercher..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9 h-9"
        />
      </div>

      {/* Status filter */}
      <Select
        value={status}
        onChange={onStatusChange}
        options={statusOptions}
        className="w-40"
      />

      {/* Type filter */}
      <Select
        value={type}
        onChange={onTypeChange}
        options={typeOptions}
        className="w-40"
      />
    </div>
  );
}
